import {
    ComponentPropsWithoutRef,
    ComponentType,
    createContext,
    ComponentPropsWithRef,
    ElementType,
    LazyExoticComponent,
    MouseEventHandler,
    PropsWithChildren,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {ReactComponent as CloseIcon24} from '@icons/CloseModal.svg';
import {createPortal} from 'react-dom';
import {CSSTransition} from 'react-transition-group';
import {useLocation} from 'react-router';
import {BadgeColor, ButtonPrimary, ButtonSecondary, TButtonComponent} from '@ui/Button';
import {Form} from '@ui/Form';
import styles from './Modal.module.scss';

type ModalComponent<T extends ComponentType<any>> = LazyExoticComponent<T> | T;

interface ModalProps<T extends ComponentType<any>> extends PropsWithChildren {
    comp: ModalComponent<T>;
    options: UseModalOptions;
}

const Modal = ({children, comp, options}: ModalProps<any>) => {
    const {close} = useModal(comp);
    const [visible, setVisible] = useState(false);
    const location = useLocation();
    const [oldLocation] = useState(location);

/*    useEffect(() => {
        if (location !== oldLocation) {
            close();
        }
    }, [location, oldLocation]);*/

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.body.style.scrollbarGutter = 'stable';

        return () => {
            document.body.style.overflow = '';
            document.body.style.scrollbarGutter = '';
        };
    }, []);

    useEffect(() => {
        requestAnimationFrame(() => setVisible(true));
    }, []);

    const nodeRef = useRef(null);
    return <div className={styles.Modal}>
        <CSSTransition
            in={visible}
            ref={nodeRef}
            classNames={{
                enter: styles.enter,
                enterActive: styles.enterActive,
                exit: styles.exit,
                exitActive: styles.exitActive,
            }}
            timeout={300}
            unmountOnExit
            onExited={() => close()}
        >
            <div className={styles.Backdrop} onClick={() => setVisible(false)}/>
        </CSSTransition>

        <CSSTransition
            in={visible}
            ref={nodeRef}
            classNames={{
                enter: styles.enter,
                enterActive: styles.enterActive,
                exit: styles.exit,
                exitActive: styles.exitActive,
            }}
            timeout={300}
            unmountOnExit
            onExited={() => close()}
        >
            <div className={styles.Wrapper}>
                <div className="click-all">
                    {options.replaceContent && children}
                </div>
                {!options.replaceContent && <div className={styles.Content} style={{
                    paddingLeft: options.paddingX as any,
                    paddingRight: options.paddingX as any,
                }}>
                    <CloseIcon24 className={`p-absolute cursor-pointer ${styles.Close}`} onClick={close}/>
                    {children}
                </div>}
            </div>
        </CSSTransition>
    </div>;
};

const ModalContext = createContext<ModalControl<any, any>>(null as any);

class ModalControl<T extends ComponentType<any>, D = ComponentPropsWithRef<T>> {
    constructor(protected setter: any) {
    }

    open(modal: ModalComponent<T>, data: D, options: UseModalOptions) {
        this.setter((modals: any[]) => [...modals, [modal, data, options]]);
    }

    close(modal: ModalComponent<T>) {
        this.setter((modals: any[]) => modals.filter(([m]) => m !== modal));
    }
}

export class SpecificModalControl<T extends ComponentType, D = ComponentPropsWithRef<T>> {
    protected _opened = false;
    get opened(): boolean {
        return this._opened;
    }

    constructor(
        protected modal: ModalComponent<T>,
        protected control: ModalControl<T, D>,
        protected options: UseModalOptions,
    ) {
        // @ts-ignore
        ['open', 'close'].forEach(method => this[method] = this[method].bind(this));
    }

    open(data: D) {
        this.control.open(this.modal, data, this.options);
        this._opened = true;
    }

    close() {
        this.control.close(this.modal);
        this._opened = false;
    }
}

export const ModalProvider = ({children}: PropsWithChildren) => {
    const [modals, setModals] = useState<[ModalComponent<any>, any, UseModalOptions][]>([]);
    const control = useMemo(() => new ModalControl(setModals), []);

    return <ModalContext.Provider value={control}>
        {children}
        {modals.map(([ModalComponent, modalProps = {}, options], i) => createPortal(<Modal key={i} comp={ModalComponent} options={options}>
            <ModalComponent {...modalProps}/>
        </Modal>, document.body))}
    </ModalContext.Provider>;
};

interface UseModalOptions {
    replaceContent?: boolean;
    paddingX?: number | null
}

export const useModal = <T extends ComponentType<any>>(comp: ModalComponent<T>, options: UseModalOptions = {replaceContent: false, paddingX: null}) => {
    const control = useContext(ModalContext);

    return useMemo(() => new SpecificModalControl<T>(comp, control, options), [options]);
};

type PolymorphicAsProp<E extends ElementType> = {
    buttonAs?: E;
};
type PolymorphicProps<E extends ElementType> = PropsWithChildren<
    ComponentPropsWithoutRef<E> & PolymorphicAsProp<E>
>;

type ModalButtonProps<T extends TButtonComponent> = PropsWithChildren<PolymorphicProps<T> & {
    modalContent: ReactNode | undefined
}>

export const ModalButton = <
    T extends TButtonComponent
>({
      modalContent,
      buttonAs: As,
      ...props
  }: ModalButtonProps<T>) => {
    // @ts-ignore
    As = As ?? ButtonPrimary;
    const modal = useModal(() => <>{modalContent}</>);

    // @ts-ignore
    return <As {...props} onClick={() => modal.open()}/>;
};
type ConfirmButtonProps<T extends TButtonComponent> = PropsWithChildren<PolymorphicProps<T> & {
    confirmText?: ReactNode,
    cancelText?: ReactNode,
    title?: ReactNode,
    color?: BadgeColor,
    onConfirm: () => any,
}>;

export const ConfirmButton = <
    T extends TButtonComponent
>({
      confirmText = 'Удалить',
      cancelText = 'Отмена',
      title = 'Удалить?',
      color = 'red',
      buttonAs: As,
      onConfirm,
      ...props
  }: ConfirmButtonProps<T>) => {

    // @ts-ignore
    As = As ?? ButtonPrimary;
    const handleClick: MouseEventHandler = e => {
        e.preventDefault();
        e.stopPropagation();
        modal.open({});
    };
    const modal = useModal(() => <div
        className='d-flex flex-column align-items-center row-gap-32'
    >
        <div className="h2">
            {title}
        </div>
        <Form className="d-flex gap-12" beforeSubmit={onConfirm} afterSubmit={modal.close}>
            <ButtonSecondary onClick={() => modal.close()}>
                {cancelText}
            </ButtonSecondary>
            <ButtonPrimary role='submit'>
                {confirmText}
            </ButtonPrimary>
        </Form>
    </div>);

    // @ts-ignore
    return <As {...props} onClick={handleClick}/>;
};
